<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import pilotAssistant from 'dashboard/api/pilot/assistant';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  inboxId: {
    type: Number,
    default: null,
  },
});

const { t } = useI18n();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const conversationId = ref(null);

// 轮询相关
let pollTimer = null;
const POLL_INTERVAL = 1500; // 1.5 秒轮询一次

// 停止轮询
const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

// 获取消息列表
const fetchMessages = async () => {
  try {
    const { data } = await pilotAssistant.getPlaygroundMessages(
      props.assistantId
    );

    if (data.conversation_id) {
      conversationId.value = data.conversation_id;
    }

    // 更新消息列表
    if (data.messages && Array.isArray(data.messages)) {
      messages.value = data.messages.map(msg => ({
        id: msg.id,
        content: msg.content,
        sender: msg.role,
        agentName: msg.agent_name,
        timestamp: msg.created_at,
      }));
    }

    // 如果正在加载，继续轮询
    if (isLoading.value) {
      pollTimer = setTimeout(fetchMessages, POLL_INTERVAL);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching playground messages:', error);
  }
};

// 重置会话
const resetConversation = async () => {
  stopPolling();
  messages.value = [];
  newMessage.value = '';
  conversationId.value = null;

  try {
    const { data } = await pilotAssistant.playgroundStartConversation({
      assistantId: props.assistantId,
      inboxId: props.inboxId,
    });

    conversationId.value = data.conversation_id;

    // 如果有开场白，添加到消息列表
    if (data.greeting) {
      messages.value.push({
        content: data.greeting,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error resetting playground:', error);
  }
};

// 发送消息
const sendMessage = async () => {
  if (!newMessage.value.trim() || isLoading.value) return;
  if (!props.inboxId) {
    // eslint-disable-next-line no-console
    console.warn('No inbox selected');
    return;
  }

  const currentMessage = newMessage.value;
  newMessage.value = '';

  // 立即添加用户消息到列表
  messages.value.push({
    content: currentMessage,
    sender: 'user',
    timestamp: new Date().toISOString(),
  });

  isLoading.value = true;

  try {
    // 发送消息到后端
    await pilotAssistant.playground({
      assistantId: props.assistantId,
      messageContent: currentMessage,
      inboxId: props.inboxId,
    });

    // 开始轮询获取 AI 响应
    fetchMessages();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error sending message:', error);
    isLoading.value = false;
  }
};

// 监听 assistantId 变化，重置会话
watch(
  () => props.assistantId,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      stopPolling();
      messages.value = [];
      conversationId.value = null;
    }
  }
);

// 组件卸载时停止轮询
onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('PILOT.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          sm
          slate
          icon="i-lucide-rotate-ccw"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('PILOT.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList :messages="messages" :is-loading="isLoading" />

    <div
      class="flex items-center mx-6 bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
    >
      <input
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('PILOT.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        @keyup.enter="sendMessage"
      />
      <NextButton
        ghost
        sm
        :disabled="!newMessage.trim() || !inboxId"
        icon="i-lucide-send"
        @click="sendMessage"
      />
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('PILOT.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
